import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Select } from 'antd';
import { Icon, Loader, TableTransfer } from 'react-inv-forms';

import {
  closePairEffectsModal,
  savePairedEffects,
  selectCardLanguage,
  selectCardVersion,
  selectStudyProgram,
  selectLearningEffects,
  changeSelectProgramOpen,
} from '../../../redux/appConfig/actions';
import { RootState } from '../../../redux/reducers';

import { effectsColumns } from '../../../settings/tableColumns';
import StringBuilder from '../../../helpers/StringBuilder';

import { FlexContainer, CustomModal, FlexBetweenContainer, TableTransferContainer } from './PairEffectsModal.style';
import pairRed from '../../../images/pairRed.svg';

const { Option } = Select;

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;

type Props = MapStateToProps & MapDispatchToProps & WithTranslation;
/**
 * Komponent modala parowania efektów kierukowych
 *
 */
export class PairEffectsModal extends React.Component<Props> {
  filterOption = (inputValue: string, option: AppConfig.LearningEffect): boolean => {
    const escaped = StringBuilder.escapeRegExp(inputValue);
    const regex = new RegExp(escaped, 'i');
    return regex.test(option.efektKierunkuKod) || regex.test(option.opisEfektuKierunkowego);
  };
  render(): React.ReactElement {
    const { pairEffectsModal, closePairEffectsModal } = this.props;
    const { studyPrograms, selectedStudyProgram, data, selectedEffects, availableEffects, selectedLang, selectedVersion } =
      pairEffectsModal;
    return (
      <CustomModal
        visible={pairEffectsModal.opened}
        okText={this.props.t('SAVE_CHANGES')}
        cancelText={this.props.t('CANCEL')}
        okButtonProps={{ disabled: !selectedEffects.length }}
        onCancel={closePairEffectsModal}
        onOk={this.props.savePairedEffects}
        title={
          <span>
            <Icon path={pairRed} />
            {this.props.t('PAIR_EFFECTS_MODAL')}
          </span>
        }
        className='cyPairEffectsModal'
      >
        <FlexBetweenContainer>
          <div>
            <h3>{this.props.t('PRZEDMIOT_NAZWA')}</h3>
            <span>{data?.przedmiotNazwa}</span>
          </div>
          <div>
            <h3>{this.props.t('PRZEDMIOT_KOD')}</h3>
            <strong>{data?.przedmiotKod}</strong>
          </div>
        </FlexBetweenContainer>
        <FlexContainer>
          <div>
            <h3>{this.props.t('PAIR_EFFECTS_MODAL_CHOOSE_CARD_LANG')}</h3>
            <Select
              onChange={this.props.selectCardLanguage}
              dropdownClassName='custom_dropdown'
              className='custom_select'
              value={selectedLang ? selectedLang : undefined}
              data-cy='selectCardLanguage'
            >
              {data &&
                data.jezykKartyLista.map((lang: any) => (
                  <Option key={lang.value} value={lang.value} className='custom_option' data-cy={`optionCardLanguage_${lang}`}>
                    {lang.name}
                  </Option>
                ))}
            </Select>
          </div>
          <div>
            <h3>{this.props.t('PAIR_EFFECTS_MODAL_CHOOSE_CARD_VERSION')}</h3>
            <Select
              onChange={this.props.selectCardVersion}
              dropdownClassName='custom_dropdown'
              className='custom_select'
              value={selectedVersion ? selectedVersion : undefined}
              data-cy='selectCardVersion'
            >
              {data &&
                data.wersjaKartyLista.map(version => (
                  <Option key={version} value={version} className='custom_option' data-cy={`optionCardVersion_${version}`}>
                    {version}
                  </Option>
                ))}
            </Select>
          </div>
          <div>
            {selectedLang && selectedVersion ? (
              <>
                <h3>{this.props.t('PAIR_EFFECTS_MODAL_CHOOSE_STUDY_PROGRAM')}</h3>
                <Select
                  open={pairEffectsModal.selectProgramOpenState}
                  onClick={() => this.props.changeSelectProgramOpen(!pairEffectsModal.selectProgramOpenState)}
                  onBlur={() => this.props.changeSelectProgramOpen(false)}
                  loading={pairEffectsModal.loading}
                  onChange={this.props.selectStudyProgram}
                  dropdownClassName='custom_dropdown'
                  className='custom_select'
                  value={selectedStudyProgram ? selectedStudyProgram : undefined}
                  notFoundContent={pairEffectsModal.loading ? null : this.props.t('NO_DATA')}
                  data-cy='selectCardProgramme'
                >
                  {studyPrograms.map((program: any) => (
                    <Option
                      key={program.programStudiowId}
                      value={program.programStudiowId}
                      className='custom_option'
                      data-cy={`optionCardProgramme_${program.kodProgramu}`}
                    >
                      {program.nazwaProgramu} | {program.kodProgramu}
                    </Option>
                  ))}
                </Select>
              </>
            ) : null}
          </div>
        </FlexContainer>
        <TableTransferContainer loading={pairEffectsModal.loadingEffects}>
          {selectedStudyProgram ? (
            <>
              <h3>{this.props.t('PAIR_EFFECTS_MODAL_LEARNING_OUTCOMES')}</h3>
              {pairEffectsModal.loadingEffects && <Loader className='loader__centered' style={{ bottom: '30%' }} />}
              <TableTransfer
                currentLanguage={this.props.currentLanguage}
                showSearch
                titles={[
                  <span key='left'>{this.props.t('PAIR_EFFECTS_MODAL_AVAILABLE_EFFECTS')}</span>,
                  <span key='right'>{this.props.t('PAIR_EFFECTS_MODAL_EFFECTS_TO_BE_PAIRED')}</span>,
                ]}
                locale={{
                  itemUnit: this.props.t('PAIR_EFFECTS_MODAL_ITEM'),
                  itemsUnit: this.props.t('PAIR_EFFECTS_MODAL_ITEMS'),
                  searchPlaceholder: this.props.t('START_TYPING_SHORT'),
                  notFoundContent: [
                    <span key='left'>{this.props.t('NO_DATA')}</span>,
                    <span key='right'>{this.props.t('NO_DATA')}</span>,
                  ],
                }}
                filterOption={this.filterOption}
                onChange={this.props.selectLearningEffects}
                dataSource={availableEffects}
                targetKeys={selectedEffects}
                columns={effectsColumns}
              ></TableTransfer>
            </>
          ) : null}
        </TableTransferContainer>
      </CustomModal>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  pairEffectsModal: state.AppConfig.pairEffectsModal,
  currentLanguage: state.AppConfig.currentLanguage,
});

const mapDispatchToProps = (dispatch: any) => ({
  closePairEffectsModal: () => dispatch(closePairEffectsModal()),
  savePairedEffects: () => dispatch(savePairedEffects()),
  selectLearningEffects: (data: number[]) => dispatch(selectLearningEffects(data)),
  selectStudyProgram: (program: string) => dispatch(selectStudyProgram(program)),
  selectCardLanguage: (lang: string) => dispatch(selectCardLanguage(lang)),
  selectCardVersion: (version: string) => dispatch(selectCardVersion(version)),
  changeSelectProgramOpen: (opened: boolean) => dispatch(changeSelectProgramOpen(opened)),
});

export default compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(PairEffectsModal) as React.ComponentType;
